/**
 * Cliente HTTP do app unificado.
 *
 * Herda do admin (`shared/api/client.ts`) o que funcionava — Bearer injetado,
 * 401 dispara UM refresh e enfileira as demais chamadas — e tira o que era
 * do admin: chaves de storage, URL fixa, redirect para `/login`. Quem decide o
 * que fazer quando a sessão morre é a casca, via `onSessionExpired`.
 */
import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import type { BloqueioAtivo } from '@eva/schemas';
import { domainConfig } from '../config';
import { storage } from './storage';

export const TOKEN_KEY = 'token';
export const REFRESH_KEY = 'refresh_token';

export interface ApiErrorShape {
  status: number;
  message: string;
  code?: string;
  details?: unknown;
}

export class ApiRequestError extends Error implements ApiErrorShape {
  status: number;
  code?: string;
  details?: unknown;
  constructor(shape: ApiErrorShape) {
    super(shape.message);
    this.name = 'ApiRequestError';
    this.status = shape.status;
    this.code = shape.code;
    this.details = shape.details;
  }
}

type Listener = () => void;
const ouvintesDeExpiracao = new Set<Listener>();

/**
 * Barreiras (paywall / adesão a programa): a API responde 403 com
 * `code: 'BARRIER_BLOCKED'` e a configuração da barreira. O portal da
 * paciente escuta aqui e abre o formulário de adesão. Chamadas que não
 * devem disparar a barreira (carrosséis, gestação) passam `skipBarrier`.
 */
export interface BarrierEvent {
  type: string;
  feature?: string;
  details?: Record<string, any> | null;
}
const ouvintesDeBarreira = new Set<(b: BarrierEvent) => void>();
export function onBarrier(fn: (b: BarrierEvent) => void): () => void {
  ouvintesDeBarreira.add(fn);
  return () => ouvintesDeBarreira.delete(fn);
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Não emite o evento de barreira num 403 BARRIER_BLOCKED. */
    skipBarrier?: boolean;
    /** Não mostra o aviso num 403 BLOQUEADO (a tela trata sozinha). */
    skipBloqueio?: boolean;
  }
}

/**
 * Bloqueios da marca (14/10/2026): a porta bloqueada responde 403
 * `BLOQUEADO` com o bloqueio em `details.bloqueio`, e quem escuta aqui (o
 * anfitrião do aviso, na raiz do app) mostra o aviso. A tela que mostra um
 * item travado chama `mostrarBloqueio` no toque, pelo mesmo caminho.
 */
type OuvinteDeBloqueio = (bloqueio: BloqueioAtivo) => void;
const ouvintesDeBloqueio = new Set<OuvinteDeBloqueio>();
export function onBloqueio(fn: OuvinteDeBloqueio): () => void {
  ouvintesDeBloqueio.add(fn);
  return () => ouvintesDeBloqueio.delete(fn);
}
export function mostrarBloqueio(bloqueio: BloqueioAtivo): void {
  for (const fn of ouvintesDeBloqueio) fn(bloqueio);
}
/** O bloqueio de um erro da API, se for um 403 `BLOQUEADO`. */
export function bloqueioDoErro(e: unknown): BloqueioAtivo | null {
  const erro = e as { code?: string; details?: { bloqueio?: BloqueioAtivo } } | null;
  return erro?.code === 'BLOQUEADO' ? erro.details?.bloqueio ?? null : null;
}

/** A casca registra aqui o que fazer quando o refresh falha (ir para o login). */
export function onSessionExpired(fn: Listener): () => void {
  ouvintesDeExpiracao.add(fn);
  return () => ouvintesDeExpiracao.delete(fn);
}

function avisarExpiracao() {
  for (const fn of ouvintesDeExpiracao) fn();
}

function traduzirErro(err: unknown): ApiRequestError {
  if (axios.isAxiosError(err)) {
    const e = err as AxiosError<any>;
    const dados = e.response?.data ?? {};
    return new ApiRequestError({
      status: e.response?.status ?? 0,
      message: dados.message || dados.error || (e.response ? `Erro ${e.response.status}` : 'Sem conexão com o servidor'),
      code: dados.code,
      details: dados.details,
    });
  }
  return new ApiRequestError({ status: 0, message: err instanceof Error ? err.message : 'Erro desconhecido' });
}

let renovando: Promise<string | null> | null = null;

async function renovarToken(): Promise<string | null> {
  const refresh = storage.get(REFRESH_KEY);
  if (!refresh) return null;
  try {
    const resposta = await axios.post(`${domainConfig().apiUrl}/api/auth/refresh`, { refreshToken: refresh });
    const dados = resposta.data?.data ?? resposta.data;
    const novoToken: string | undefined = dados?.accessToken;
    const novoRefresh: string | undefined = dados?.refreshToken;
    if (!novoToken) return null;
    storage.set(TOKEN_KEY, novoToken);
    if (novoRefresh) storage.set(REFRESH_KEY, novoRefresh);
    return novoToken;
  } catch {
    return null;
  }
}

/** Exposto para o refresh proativo da sessão (antes de o token expirar). */
export function refreshAccessToken(): Promise<string | null> {
  if (!renovando) {
    renovando = renovarToken().finally(() => {
      renovando = null;
    });
  }
  return renovando;
}

function criarInstancia(): AxiosInstance {
  const instancia = axios.create({ timeout: 30_000 });

  instancia.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
    cfg.baseURL = domainConfig().apiUrl;
    const token = storage.get(TOKEN_KEY);
    if (token && !cfg.headers.Authorization) {
      cfg.headers.Authorization = `Bearer ${token}`;
    }
    return cfg;
  });

  instancia.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      const original = error.config as (AxiosRequestConfig & { _retentado?: boolean }) | undefined;
      const status = error.response?.status;

      const corpo: any = error.response?.data;
      if (status === 403 && corpo?.code === 'BLOQUEADO' && corpo?.details?.bloqueio && !original?.skipBloqueio) {
        mostrarBloqueio(corpo.details.bloqueio as BloqueioAtivo);
      }
      if (status === 403 && corpo?.code === 'BARRIER_BLOCKED' && !original?.skipBarrier) {
        const b = corpo.barrier ?? {};
        const evento: BarrierEvent = { type: b.barrierType ?? b.type ?? corpo.type ?? 'NONE', feature: b.feature ?? corpo.feature, details: b.data ?? b.details ?? corpo.details ?? null };
        for (const fn of ouvintesDeBarreira) fn(evento);
      }
      const url = String(original?.url ?? '');
      const ehRotaDeAuth = url.includes('/api/auth/login') || url.includes('/api/auth/refresh');

      if (status === 401 && original && !original._retentado && !ehRotaDeAuth) {
        original._retentado = true;
        const novo = await refreshAccessToken();
        if (novo) {
          original.headers = { ...(original.headers ?? {}), Authorization: `Bearer ${novo}` };
          return instancia.request(original);
        }
        storage.remove(TOKEN_KEY);
        storage.remove(REFRESH_KEY);
        avisarExpiracao();
      }
      throw traduzirErro(error);
    }
  );

  return instancia;
}

const http = criarInstancia();

/** Chamadas tipadas. `data` é o envelope `{ data }` da API quando existe. */
export const api = {
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const r = await http.get(url, config);
    return desembrulhar<T>(r.data);
  },
  async post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const r = await http.post(url, body, config);
    return desembrulhar<T>(r.data);
  },
  async put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const r = await http.put(url, body, config);
    return desembrulhar<T>(r.data);
  },
  async patch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const r = await http.patch(url, body, config);
    return desembrulhar<T>(r.data);
  },
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const r = await http.delete(url, config);
    return desembrulhar<T>(r.data);
  },
  /** Resposta crua, para endpoints que não usam o envelope (`meta` junto de `data`). */
  raw: http,
};

/**
 * Chaves que só acompanham o envelope. A API não é uniforme: há `{data}`,
 * `{message, data}`, `{success, data}` e `{data, meta}` — e respostas sem
 * envelope (`{items, version}` do chat, `{tools}` do cuidado), que ficam
 * como estão. O mobile fazia `res.data?.data ?? res.data`; aqui a regra é a
 * mesma, só que declarada: desembrulha quando TODAS as outras chaves são
 * de envelope.
 */
const CHAVES_DE_ENVELOPE = new Set(['data', 'message', 'meta', 'success', 'status', 'pagination', 'total', 'count', 'page', 'limit', 'timestamp']);

function desembrulhar<T>(corpo: any): T {
  if (corpo && typeof corpo === 'object' && !Array.isArray(corpo) && 'data' in corpo && Object.keys(corpo).every((k) => CHAVES_DE_ENVELOPE.has(k))) {
    return corpo.data as T;
  }
  return corpo as T;
}

/** Exposto só para o teste de contrato do envelope. */
export const desembrulharParaTeste = desembrulhar;
